/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import appConfig from '../stores/config';
import { ANALYTICS, LEFT_NAV_KEYS } from '../utils/enums';
import {
    DESKTOP_APP_FILTERS, UPDATES_APP_FILTERS, APP_PLATFORM, ACTION_ID,
} from '../utils/modelMapper/appModelMapper/appModelConstants';
import { ENTITLEMENTS } from '../utils/modelMapper/appModelMapper/appJsonConstants';


export function getEntitlementStatus(appModel) {
    let entitlementStatus = ENTITLEMENTS.NO_ENTITLEMENT;
    if (appModel.platform === APP_PLATFORM.WEB
        || appModel.platform === APP_PLATFORM.MOBILE) {
        entitlementStatus = ENTITLEMENTS.INFORMATION_NOT_AVAILABLE;
    } else if (appModel.entitlementInfo && appModel.entitlementInfo.status) {
        entitlementStatus = appModel.entitlementInfo.status;
    }
    return entitlementStatus;
}

export function isAppInPlan(appModel) {
    let appInPlan = true;
    const entitlementStatus = getEntitlementStatus(appModel);
    if ((entitlementStatus === ENTITLEMENTS.TRIAL_AVAILABLE)
        || (entitlementStatus === ENTITLEMENTS.TRIAL_ACTIVE)
        || (entitlementStatus === ENTITLEMENTS.TRIAL_EXPIRED)
        || (entitlementStatus === ENTITLEMENTS.LICENSE_EXPIRED)
        || (entitlementStatus === ENTITLEMENTS.NO_ENTITLEMENT)) {
        appInPlan = false;
    }
    return appInPlan;
}

export function getAppInstallationStatus(appModel, appSectionId) {
    let isAppInstalled = false;
    let isAppSubscribed = false;
    if (appModel.subHeadingFilter
      && appModel.subHeadingFilter.indexOf(DESKTOP_APP_FILTERS.INSTALLED) !== -1) {
        isAppInstalled = true;
    }
    if (isAppInPlan(appModel)) {
        isAppSubscribed = true;
    }

    const value = [];
    value.push(
        isAppInstalled
            ? ANALYTICS.INSTALLATION_STATE.INSTALLED
            : ANALYTICS.INSTALLATION_STATE.NOT_INSTALLED
    );
    value.push(
        isAppSubscribed
            ? ANALYTICS.ENTITLEMENT.ENTITLED
            : ANALYTICS.ENTITLEMENT.NOT_ENTITLED
    );
    let compatibility;
    if (appModel.isIncompatible) {
        compatibility = ANALYTICS.COMPATIBILITY.NOT_COMPATIBLE;
    } else if (appModel.isIncompatible === false) {
        compatibility = ANALYTICS.COMPATIBILITY.COMPATIBLE;
    }
    if (compatibility) {
        value.push(compatibility);
    }
    if (appSectionId) {
        const { progress, appSectionBasedFields } = appModel;
        let ctaList = [];
        if (progress) {
            ctaList = [ACTION_ID.CANCEL];
        } else {
            const selectedField = appSectionBasedFields.find((
                { appSectionFilter: appSectionFilterArray }
            ) => appSectionFilterArray.includes(appSectionId));
            if (selectedField) {
                const { buttonList = [] } = selectedField;
                ctaList = buttonList.filter(button => button.actionType.variant !== 'secondary')
                    .map(button => button.actionId);
            }
        }
        if (ctaList.length) {
            value.push(ctaList.join());
        }
    }
    return value.join(':');
}

// TODO: update this func and use subHeadingFilter to get values
// such as 'New Updates' and 'Updated Recently'
export function getAppContentType(dataObject) {
    let status = null;
    const { appSectionFilter, appSection } = dataObject;
    let appSectionId = appSectionFilter;
    if (!appSectionId && appSection && appSection.id) {
        appSectionId = appSection.id;
    }
    if (appSectionId === LEFT_NAV_KEYS.UPDATES_KEY) {
        // For Updates section content type needs to be set as section header
        if (dataObject.app.subHeadingFilter
                && dataObject.app.subHeadingFilter.includes(UPDATES_APP_FILTERS.NEW_UPDATES)) {
            status = ANALYTICS.UPDATE_SECTIONS.NEW_UPDATES;
        } else if (dataObject.app.subHeadingFilter
                && dataObject.app.subHeadingFilter.includes(UPDATES_APP_FILTERS.UPDATED_RECENTLY)) {
            status = ANALYTICS.UPDATE_SECTIONS.UPDATED_RECENTLY;
        }
    }
    if (status == null) {
        status = getAppInstallationStatus(dataObject.app, appSectionId);
    }
    return status;
}

export default function appTileAnalytics(eventName, dataObject = {}) {
    const obj = {};
    const componentParams = {};
    obj.event = {
        workflow: 'Apps',
        type: 'click',
        subtype: dataObject.actionId,
        subcategory: dataObject.appSectionFilter,
        guid: dataObject.guid,
    };
    if (dataObject.app) {
        obj.content = {
            id: dataObject.app.sapcode,
            name: dataObject.app.appFamilyName,
            category: dataObject.app.platform,
            version: dataObject.app.codexVersion,
            size: dataObject.contentSize,
            extension: dataObject.contentExt,
        };
        obj.ui = {
            filter: 'app-tile',
        };
        obj.content.type = getAppContentType(dataObject);
        if (dataObject.app.isIncompatible === true) {
            obj.event.value = 'Not Compatible';
        }
    }
    componentParams.sectionId = dataObject.appSectionFilter;
    componentParams.source = ANALYTICS.APPS;
    componentParams.actionId = dataObject.actionId;

    switch (eventName) {
    case ANALYTICS.EVENTS.APP_BUTTON_CLICK:
        obj.ui.view_type = dataObject.viewType;
        // any difference in fields need to be added
        break;
    case ANALYTICS.EVENTS.APP_MENU_CLICK:
        obj.ui.view_type = 'Operations Menu';
        break;
    case ANALYTICS.EVENTS.APP_TILE_CLICK:
        obj.ui.view_type = dataObject.viewType;
        // any difference in fields need to be added
        break;
    case ANALYTICS.EVENTS.MORE_INFO_CLICK:
        if (dataObject.actionId === ACTION_ID.ERROR_INFO) {
            if (dataObject.app && dataObject.app.errorDetails
                    && dataObject.app.errorDetails.errorAction) {
                obj.event.error_type = dataObject.app.errorDetails.errorAction;
            }
        }
        // any difference in fields need to be added
        break;
    case ANALYTICS.EVENTS.SEE_MORE_CLICK:
        obj.event.subcategory = 'Updates';
        // set external URL in content.action
        if (dataObject.seeMoreLinkUrl && dataObject.app.productWhatsNewUrl) {
            obj.content.action = dataObject.app.productWhatsNewUrl;
        }
        break;
    case ANALYTICS.EVENTS.APP_PROGRESS_CANCEL:
        obj.event.subtype = 'cancel';
        obj.event.value = dataObject.progress;
        obj.ui.view_type = dataObject.viewType;
        break;
    case ANALYTICS.EVENTS.UPDATE_AVAILABLE:
        break;
    default:
        return;
    }
    appConfig.analyticsCallback({ componentParams, analyticObjectList: [obj] });
}
